import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConsoleShell, PageHead } from "../components/ConsoleShell.js";
import { Tabs, TabPanel, BtnLink, Btn, Banner } from "../components/index.js";
import { EmptyState } from "../components/EmptyState.js";
import { Icon } from "../components/Icon.js";
import { isAuthed, isOffline } from "../lib/api.js";
import { listQueue, uploadQueued, removeQueued, type QueueItem } from "../lib/offline.js";

function fmtBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

function fmtDate(ts: number): string {
  return new Date(ts).toLocaleString("ja-JP", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/**
 * 受信箱 — アップロードキュー: オフライン撮影分を送信前にプレビューして、個別に送信/破棄できる。
 * サムネタップで中身（動画/画像）を確認 → 「この内容で送信」。送信中は進捗をライブ表示し、
 * 失敗理由はその場に出す（黙って失敗しない）。
 */
export function Inbox() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("queue");
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [preview, setPreview] = useState<QueueItem | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = () => listQueue().then(setQueue).catch(() => setQueue([]));
  useEffect(() => {
    void refresh();
  }, []);

  // Blob preview object URLs — recreated per queue snapshot, revoked on replace/unmount.
  const [urls, setUrls] = useState<Record<string, string>>({});
  useEffect(() => {
    const map: Record<string, string> = {};
    for (const q of queue) {
      try {
        map[q.id] = URL.createObjectURL(q.blob);
      } catch {
        /* blob unavailable */
      }
    }
    setUrls(map);
    return () => {
      for (const u of Object.values(map)) URL.revokeObjectURL(u);
    };
  }, [queue]);

  const offline = isOffline();
  const authed = isAuthed();

  async function send(item: QueueItem) {
    if (sendingId) return;
    setSendingId(item.id);
    // Live progress: uploadQueued patches progress into IndexedDB — poll it while sending.
    pollRef.current = setInterval(() => void refresh(), 600);
    let manualId: string | null = null;
    try {
      manualId = await uploadQueued(item);
    } catch {
      /* failure reason is stored on the item and rendered below */
    }
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    await refresh();
    setSendingId(null);
    // Same flow as a normal capture: land on the processing screen, which auto-advances to the
    // keyframe picker (クリップ確認) when the AI analysis finishes.
    if (manualId && !manualId.startsWith("local_")) navigate(`/processing/${manualId}`);
  }
  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current);
    },
    [],
  );

  async function discard(item: QueueItem) {
    if (!confirm("この撮影データを破棄しますか？この操作は取り消せません。")) return;
    await removeQueued(item.id);
    await refresh();
  }

  const pending = queue.filter((q) => q.status !== "done").length;

  return (
    <ConsoleShell breadcrumb={["TEBIKI", "受信箱"]}>
      <PageHead title={<span className="hl">受信箱</span>} />
      <Tabs
        active={tab}
        onChange={setTab}
        ariaLabel="受信箱ビュー"
        tabs={[
          { id: "queue", label: "アップロードキュー", badge: pending || undefined },
          { id: "notif", label: "通知" },
        ]}
      />

      <TabPanel id="queue" active={tab}>
        {pending > 0 && offline ? (
          <Banner variant="warn" icon={<Icon name="offline" />} className="reveal">
            <span>オフラインです。接続が戻ると自動送信されます（手動送信も接続後に可能になります）。</span>
          </Banner>
        ) : pending > 0 && !authed ? (
          <Banner variant="warn" className="reveal">
            <span>ログインが必要です。ログインすると自動送信されます。</span>
          </Banner>
        ) : null}
        {queue.length === 0 ? (
          <div className="list reveal">
            <EmptyState icon={<Icon name="approval" size={30} />} title="送信待ちはありません">
              オフライン撮影分はWi-Fi接続時に自動送信されます。
            </EmptyState>
          </div>
        ) : (
          <div className="list reveal">
            <div className="lrow lhead ucols" style={ucols}>
              <span />
              <span>ファイル</span>
              <span>進捗</span>
              <span>状態</span>
              <span />
            </div>
            {queue.map((q) => {
              const isVideo = q.contentType.startsWith("video");
              const url = urls[q.id];
              const busy = sendingId === q.id || q.status === "uploading";
              return (
                <div key={q.id} className="lrow ucols" style={{ ...ucols, alignItems: "center" }}>
                  <button
                    type="button"
                    onClick={() => setPreview(q)}
                    aria-label={`${q.filename} をプレビュー`}
                    style={{ padding: 0, border: "1px solid var(--line)", background: "#15171d", borderRadius: 10, overflow: "hidden", cursor: "pointer", width: 64, height: 48, display: "grid", placeItems: "center" }}
                  >
                    {url && isVideo ? (
                      <video src={url} muted playsInline preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : url ? (
                      <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <Icon name="capture" size={20} style={{ color: "var(--muted)" }} />
                    )}
                  </button>
                  <div style={{ minWidth: 0 }}>
                    <button
                      type="button"
                      className="jp"
                      onClick={() => setPreview(q)}
                      style={{ padding: "10px 8px 10px 0", margin: "-10px 0", border: 0, background: "none", cursor: "pointer", font: "inherit", fontWeight: 700, textAlign: "left" }}
                    >
                      {q.filename}
                    </button>
                    <div className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>
                      {fmtBytes(q.blob.size)} · 撮影 {fmtDate(q.createdAt)}
                    </div>
                    {q.status === "error" && q.error ? (
                      <div className="jp" role="alert" style={{ fontSize: 12, color: "var(--danger)", marginTop: 2 }}>
                        {q.error}
                      </div>
                    ) : null}
                  </div>
                  <div className="pmini">
                    <i style={{ width: `${Math.round((q.progress ?? 0) * 100)}%`, background: q.status === "error" ? "var(--danger)" : q.status === "pending" ? "var(--warn)" : undefined }} />
                  </div>
                  <span className="tag tag--dot" style={{ color: busy ? "var(--hi-deep)" : q.status === "error" ? "var(--danger)" : q.status === "done" ? "var(--ok)" : "var(--warn)" }}>
                    {busy ? `送信中 ${Math.round((q.progress ?? 0) * 100)}%` : q.status === "error" ? "失敗" : q.status === "done" ? "送信済み" : "待機中"}
                  </span>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {!busy && q.status === "done" && q.manualId && !q.manualId.startsWith("local_") ? (
                      <button
                        className="tag tag--hi"
                        style={{ border: 0 }}
                        onClick={() => navigate(`/processing/${q.manualId}`)}
                      >
                        クリップ確認へ
                      </button>
                    ) : null}
                    {!busy && q.status !== "done" ? (
                      <button
                        className="tag tag--hi"
                        style={{ border: 0 }}
                        disabled={offline || !authed || sendingId !== null}
                        title={offline ? "オフラインです" : !authed ? "ログインが必要です" : undefined}
                        onClick={() => void send(q)}
                      >
                        {q.status === "error" ? "再送信" : "送信"}
                      </button>
                    ) : null}
                    {!busy ? (
                      <button
                        className="tag"
                        style={{ border: "1px solid var(--line-2)" }}
                        aria-label={`${q.filename} を破棄`}
                        onClick={() => void discard(q)}
                      >
                        <Icon name="close" size={16} />
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </TabPanel>

      <TabPanel id="notif" active={tab}>
        <div className="list reveal">
          <EmptyState
            icon={<Icon name="inbox" size={30} />}
            title="通知は準備中です"
            action={
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
                <BtnLink to="/approval" variant="hi">
                  承認待ちを見る
                </BtnLink>
                <BtnLink to="/feedback">フィードバック</BtnLink>
              </div>
            }
          >
            承認依頼・現場からのフィードバック・改訂の公開・翻訳完了を、ここでまとめてお知らせする予定です。
            それまでは各画面でご確認ください。
          </EmptyState>
        </div>
      </TabPanel>

      {/* ---- 送信前プレビュー: 中身を確認してから送る ---- */}
      {preview ? (
        <div
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPreview(null);
          }}
          style={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(18,22,27,.5)", display: "grid", placeItems: "center", padding: 16 }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="送信前のプレビュー"
            onKeyDown={(e) => {
              if (e.key === "Escape") setPreview(null);
            }}
            className="reveal"
            style={{ width: "min(640px, 100%)", maxHeight: "92dvh", overflowY: "auto", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 16, boxShadow: "var(--shadow-3)", padding: 18 }}
          >
            <div className="spread" style={{ alignItems: "center", marginBottom: 12 }}>
              <b className="jp" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{preview.filename}</b>
              <button className="tag" type="button" aria-label="閉じる" onClick={() => setPreview(null)}>
                <Icon name="close" size={16} />
              </button>
            </div>
            {urls[preview.id] && preview.contentType.startsWith("video") ? (
              <video src={urls[preview.id]} controls playsInline style={{ width: "100%", maxHeight: "55vh", borderRadius: 12, background: "#000" }} />
            ) : urls[preview.id] ? (
              <img src={urls[preview.id]} alt={preview.filename} style={{ maxWidth: "100%", maxHeight: "55vh", borderRadius: 12, display: "block", margin: "0 auto" }} />
            ) : (
              <p className="jp" style={{ color: "var(--muted)" }}>プレビューを表示できません。</p>
            )}
            <div className="mono" style={{ fontSize: 12, color: "var(--muted)", margin: "10px 0 0" }}>
              {fmtBytes(preview.blob.size)} · 撮影 {fmtDate(preview.createdAt)} · {preview.source.sourceType === "video" ? "動画" : "画像"}
            </div>
            {preview.status === "error" && preview.error ? (
              <p className="jp" role="alert" style={{ margin: "8px 0 0", fontSize: 13, color: "var(--danger)" }}>
                {preview.error}
              </p>
            ) : null}
            <div className="row" style={{ gap: 10, marginTop: 16, flexWrap: "wrap" }}>
              {preview.status === "done" && preview.manualId && !preview.manualId.startsWith("local_") ? (
                <Btn
                  variant="hi"
                  icon={<Icon name="target" size={15} />}
                  onClick={() => navigate(`/processing/${preview.manualId}`)}
                >
                  クリップ確認へ
                </Btn>
              ) : null}
              {preview.status !== "done" ? (
                <Btn
                  variant="hi"
                  icon={<Icon name="upload" size={15} />}
                  disabled={offline || !authed || sendingId !== null}
                  title={offline ? "オフラインです" : !authed ? "ログインが必要です" : undefined}
                  onClick={() => {
                    const item = preview;
                    setPreview(null);
                    void send(item);
                  }}
                >
                  この内容で送信
                </Btn>
              ) : null}
              <Btn
                variant="ghost"
                icon={<Icon name="close" size={15} />}
                onClick={() => {
                  const item = preview;
                  setPreview(null);
                  void discard(item);
                }}
              >
                破棄
              </Btn>
              <span className="grow" />
              <Btn variant="ghost" onClick={() => setPreview(null)}>閉じる</Btn>
            </div>
          </div>
        </div>
      ) : null}
    </ConsoleShell>
  );
}

const ucols = { gridTemplateColumns: "64px minmax(0,1fr) 120px 110px auto", gap: 12 } as const;
